import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe lazily to prevent module-level errors
let stripePromise = null;

export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');
  }
  return stripePromise;
};

// Subscription plans configuration
export const SUBSCRIPTION_PLANS = {
  FREE: {
    id: 'free',
    name: 'Free',
    price: 0,
    currency: 'usd',
    interval: 'month',
    features: [
      '3 bean uploads per day',
      'Basic leaderboards',
      'Standard profiles',
      'Community features'
    ],
    limitations: {
      dailyUploads: 3,
      analytics: false,
      adFree: false,
      premiumBadge: false,
      superLikes: 0
    }
  },
  PREMIUM_MONTHLY: {
    id: 'premium_monthly',
    name: 'OnlyBeanz Premium',
    price: 4.99,
    currency: 'usd',
    interval: 'month',
    stripeProductId: process.env.REACT_APP_STRIPE_PREMIUM_MONTHLY_PRICE_ID,
    features: [
      'Unlimited bean uploads',
      'Advanced analytics',
      'Ad-free experience',
      'Premium badge',
      '10 super likes per day',
      'Priority support'
    ],
    limitations: {
      dailyUploads: -1, // unlimited
      analytics: true,
      adFree: true,
      premiumBadge: true,
      superLikes: 10
    }
  },
  PREMIUM_YEARLY: {
    id: 'premium_yearly',
    name: 'OnlyBeanz Premium (Annual)',
    price: 39.99,
    currency: 'usd',
    interval: 'year',
    stripeProductId: process.env.REACT_APP_STRIPE_PREMIUM_YEARLY_PRICE_ID,
    savings: '33% OFF',
    features: [
      'Unlimited bean uploads',
      'Advanced analytics',
      'Ad-free experience',
      'Premium badge',
      '15 super likes per day',
      'Priority support',
      'Annual subscriber perks'
    ],
    limitations: {
      dailyUploads: -1, // unlimited
      analytics: true,
      adFree: true,
      premiumBadge: true,
      superLikes: 15
    }
  }
};

// Helper function to get user's current plan
export const getUserPlan = (user) => {
  if (!user || !user.subscription) {
    return SUBSCRIPTION_PLANS.FREE;
  }
  
  return SUBSCRIPTION_PLANS[user.subscription.planId] || SUBSCRIPTION_PLANS.FREE;
};

// Check if user can perform action based on their plan
export const canUserUpload = (user) => {
  const plan = getUserPlan(user);
  
  if (plan.limitations.dailyUploads === -1) {
    return true; // unlimited
  }
  
  // Check daily upload count (you'll need to track this in Firebase)
  const today = new Date().toDateString();
  const todayUploads = user.dailyUploads?.[today] || 0;
  
  return todayUploads < plan.limitations.dailyUploads;
};

// Stripe checkout session creation
export const createCheckoutSession = async (priceId, userId) => {
  try {
    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        userId,
        successUrl: `${window.location.origin}/subscription-success`,
        cancelUrl: `${window.location.origin}/pricing`
      }),
    });

    const session = await response.json();
    
    if (session.error) {
      throw new Error(session.error);
    }

    // Redirect directly to Stripe Checkout
    if (session.url) {
      window.location.href = session.url;
    } else {
      const stripe = await getStripe();
      const { error } = await stripe.redirectToCheckout({
        sessionId: session.sessionId,
      });

      if (error) {
        throw new Error(error.message);
      }
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

// Customer portal session (for managing subscription)
export const createPortalSession = async (customerId) => {
  try {
    const response = await fetch('/.netlify/functions/create-portal-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId,
        returnUrl: window.location.origin
      }),
    });

    const session = await response.json();
    
    if (session.url) {
      window.location.href = session.url;
    }
  } catch (error) {
    console.error('Error creating portal session:', error);
    throw error;
  }
};